/**
 * Permission lookup hook. Wraps the active team's userPermissions array
 * (sourced from backend GET /team/:teamId) with a `can(permission)` helper.
 *
 * Falls back to the client-side role matrix in shared/constants/permissions.js
 * when the backend payload hasn't loaded yet, so UI doesn't flash open before
 * permissions resolve.
 */
import { useMemo, useCallback } from 'react';
import { useTeam } from 'shared/context/teamContext';
import { PERMISSIONS, getRolePermissions } from 'shared/constants/permissions';

export { PERMISSIONS };

export function usePermissions() {
  const { selectedTeam } = useTeam();

  const role = String(
    selectedTeam?.userRole || selectedTeam?.role || 'member'
  ).toLowerCase();

  const permissions = useMemo(() => {
    // An empty array is a real answer (no permissions), not a missing one.
    if (Array.isArray(selectedTeam?.userPermissions)) {
      return new Set(selectedTeam.userPermissions);
    }
    // Fallback to client-side matrix while team data loads / for legacy payloads.
    return new Set(getRolePermissions(role));
  }, [selectedTeam, role]);

  const can = useCallback((permission) => permissions.has(permission), [permissions]);

  const canAny = useCallback(
    (perms) => (Array.isArray(perms) ? perms.some((p) => permissions.has(p)) : false),
    [permissions]
  );

  return { role, can, canAny, permissions };
}

export default usePermissions;
